import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import {
  saveQuestion,
  selectIsSavingQuestion,
  selectSaveQuestionError,
} from "../../../redux/slices/question-bank-slice";
import {
  createTemplate,
  selectIsCreatingTemplate,
  selectCreateTemplateError,
} from "../../../redux/slices/question-template-slice";

const QUESTION_TYPES = [
  { value: "VIDEO", label: "Video", icon: "🎥" },
  { value: "MCQ", label: "MCQ", icon: "📝" },
  { value: "BEHAVIORAL", label: "Behavioral", icon: "🗣" },
  { value: "SKILL", label: "Skill", icon: "⭐" },
  { value: "LIP_SYNC", label: "Lip-sync", icon: "👄" },
  { value: "PRACTICE", label: "Practice", icon: "🎯" },
  { value: "CODING", label: "Coding", icon: "💻" },
];

const RESPONSE_TYPES = ["text", "audio", "video"];
const DIFFICULTIES = ["EASY", "MEDIUM", "HARD", "EXPERT"];
const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "hi", label: "Hindi" },
  { code: "es", label: "Spanish" },
  { code: "fr", label: "French" },
];

// CODING_LANGUAGES removed — coding question type now redirects to the
// dedicated editor at /admin/coding-questions/{id} which has its own
// language list aligned with Judge0 ids.
const SKILL_SCALES = [3, 5, 10];

const emptyQuestion = () => ({
  questionId: null,
  questionText: "",
  questionType: "VIDEO",
  responseType: "video",
  competency: "",
  defaultLanguage: "en",
  difficulty: "MEDIUM",
  responseTimeInMinutes: 1,
  responseTimeInSeconds: 30,
  tags: [],
  questionOptions: [],
  correctOption: "",
  lipSyncVideoLink: "",
  terminateInterview: false,
  isMoveToQuestionBank: true,
  isPractice: false,
  // Feature #2 — type-specific config. Shape depends on questionType.
  //   CODING → { language, starterCode, testCases: [{input, expected, hidden}] }
  //   SKILL  → { ratingScale }
  //   others → null
  typeMetadata: null,
});

export default function QuestionEditPanel({
  open,
  jobId,
  interviewRound,
  question,
  onClose,
  onSaved,
}) {
  const dispatch = useDispatch();
  const saving = useSelector(selectIsSavingQuestion);
  const saveError = useSelector(selectSaveQuestionError);
  const creatingTemplate = useSelector(selectIsCreatingTemplate);
  const createTemplateError = useSelector(selectCreateTemplateError);

  const [form, setForm] = useState(emptyQuestion());
  const [tagInput, setTagInput] = useState("");
  const [templateSaved, setTemplateSaved] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({ ...emptyQuestion(), ...(question || {}) });
      setTagInput("");
      setTemplateSaved(false);
    }
  }, [open, question]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addTag = () => {
    const t = tagInput.trim();
    if (!t) return;
    if (form.tags.includes(t)) {
      setTagInput("");
      return;
    }
    setForm((f) => ({ ...f, tags: [...f.tags, t] }));
    setTagInput("");
  };

  const removeTag = (t) => setForm((f) => ({ ...f, tags: f.tags.filter((x) => x !== t) }));

  const addOption = () =>
    setForm((f) => ({ ...f, questionOptions: [...f.questionOptions, ""] }));

  const updateOption = (i, v) =>
    setForm((f) => ({
      ...f,
      questionOptions: f.questionOptions.map((o, idx) => (idx === i ? v : o)),
    }));

  const removeOption = (i) =>
    setForm((f) => ({
      ...f,
      questionOptions: f.questionOptions.filter((_, idx) => idx !== i),
    }));

  const canSave = () => {
    if (!form.questionText.trim()) return false;
    if (!jobId || !interviewRound) return false;
    if (form.questionType === "MCQ") {
      if (form.questionOptions.length < 2) return false;
      if (!form.correctOption) return false;
    }
    if (form.questionType === "LIP_SYNC" && !form.lipSyncVideoLink.trim()) {
      return false;
    }
    return true;
  };

  const navigate = useNavigate();

  const handleSave = async () => {
    if (!canSave() || saving) return;
    const result = await dispatch(saveQuestion({ jobId, interviewRound, question: form }));
    if (saveQuestion.fulfilled.match(result)) {
      // CODING questions need their starter/test-case config in the
      // amplehire_coding_question_t side-table. After save, jump straight
      // into that editor so the question is actually usable end-to-end.
      // Backend echoes the saved DTO as `questionsdto` (see InterviewQuestionResponse).
      const savedQuestionId =
        result.payload?.questionsdto?.questionId ||
        result.payload?.questionsdto?.id ||
        result.payload?.questionId ||
        result.payload?.id ||
        form.questionId;
      if (form.questionType === "CODING" && savedQuestionId) {
        onSaved?.();
        onClose?.();
        navigate(`/admin/coding-questions/${savedQuestionId}`);
        return;
      }
      onSaved?.();
      onClose?.();
    }
  };

  const handleSaveAsTemplate = async () => {
    if (!form.questionText.trim() || creatingTemplate) return;
    const defaultName = (form.questionText || "").slice(0, 80) || "Untitled template";
    const name = window.prompt("Template name:", defaultName);
    if (name === null) return; // user cancelled
    const result = await dispatch(createTemplate({ form, name }));
    if (createTemplate.fulfilled.match(result)) {
      setTemplateSaved(true);
    }
  };

  const handleSwitchToAdvanced = () => {
    const params = new URLSearchParams();
    if (form.questionId) params.set("questionId", String(form.questionId));
    if (jobId) params.set("jobId", String(jobId));
    if (interviewRound) params.set("interviewRound", interviewRound);
    const qs = params.toString();
    window.location.href = `/admin/add-questions${qs ? `?${qs}` : ""}`;
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            style={backdrop}
          />
          <motion.aside
            initial={{ x: 480 }}
            animate={{ x: 0 }}
            exit={{ x: 480 }}
            transition={{ type: "tween", duration: 0.22 }}
            style={panel}
          >
            <header style={hdr}>
              <div>
                <div style={hdrTitle}>
                  {form.questionId ? "Edit question" : "New question"}
                </div>
                <div style={hdrSub}>
                  {interviewRound ? `${interviewRound} · ` : ""}
                  Common-case form — advanced features open the full editor
                </div>
              </div>
              <button onClick={onClose} style={closeBtn} aria-label="Close">
                ×
              </button>
            </header>

            <div style={body}>
              <Field label="Question text">
                <textarea
                  value={form.questionText}
                  onChange={(e) => update("questionText", e.target.value)}
                  placeholder="What would the candidate read or hear?"
                  rows={3}
                  style={textarea}
                />
              </Field>

              <Field label="Type">
                <div style={chipRow}>
                  {QUESTION_TYPES.map((t) => (
                    <Chip
                      key={t.value}
                      active={form.questionType === t.value}
                      onClick={() => update("questionType", t.value)}
                    >
                      <span style={{ marginRight: 4 }}>{t.icon}</span>
                      {t.label}
                    </Chip>
                  ))}
                </div>
              </Field>

              <Row>
                <Field label="Response type">
                  <select
                    value={form.responseType}
                    onChange={(e) => update("responseType", e.target.value)}
                    style={input}
                  >
                    {RESPONSE_TYPES.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Language">
                  <select
                    value={form.defaultLanguage}
                    onChange={(e) => update("defaultLanguage", e.target.value)}
                    style={input}
                  >
                    {LANGUAGES.map((l) => (
                      <option key={l.code} value={l.code}>
                        {l.label}
                      </option>
                    ))}
                  </select>
                </Field>
              </Row>

              <Row>
                <Field label="Time (minutes)">
                  <input
                    type="number"
                    min="0"
                    value={form.responseTimeInMinutes}
                    onChange={(e) =>
                      update("responseTimeInMinutes", e.target.value)
                    }
                    style={input}
                  />
                </Field>
                <Field label="Time (seconds)">
                  <input
                    type="number"
                    min="0"
                    max="59"
                    value={form.responseTimeInSeconds}
                    onChange={(e) =>
                      update("responseTimeInSeconds", e.target.value)
                    }
                    style={input}
                  />
                </Field>
              </Row>

              <Field label="Competency">
                <input
                  type="text"
                  value={form.competency}
                  onChange={(e) => update("competency", e.target.value)}
                  placeholder="e.g. React, System design"
                  style={input}
                />
              </Field>

              <Field label="Difficulty">
                <div style={chipRow}>
                  {DIFFICULTIES.map((d) => (
                    <Chip
                      key={d}
                      active={form.difficulty === d}
                      onClick={() => update("difficulty", d)}
                    >
                      {d}
                    </Chip>
                  ))}
                </div>
              </Field>

              <Field label="Tags">
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 6 }}>
                  {form.tags.map((t) => (
                    <span key={t} style={tagPill}>
                      {t}
                      <button onClick={() => removeTag(t)} style={tagX}>×</button>
                    </span>
                  ))}
                </div>
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === ",") {
                      e.preventDefault();
                      addTag();
                    }
                  }}
                  placeholder="Type a tag, Enter to add"
                  style={input}
                />
              </Field>

              {form.questionType === "MCQ" && (
                <Field label="Options (mark the correct one)">
                  {form.questionOptions.map((opt, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                      <input
                        type="radio"
                        name="correctOption"
                        checked={form.correctOption === String(i)}
                        onChange={() => update("correctOption", String(i))}
                      />
                      <input
                        type="text"
                        value={opt}
                        onChange={(e) => updateOption(i, e.target.value)}
                        placeholder={`Option ${i + 1}`}
                        style={{ ...input, flex: 1 }}
                      />
                      <button onClick={() => removeOption(i)} style={ghostBtnSm}>×</button>
                    </div>
                  ))}
                  <button onClick={addOption} style={ghostBtnSm}>+ Add option</button>
                </Field>
              )}

              {form.questionType === "LIP_SYNC" && (
                <Field label="Reference video URL">
                  <input
                    type="url"
                    value={form.lipSyncVideoLink}
                    onChange={(e) => update("lipSyncVideoLink", e.target.value)}
                    placeholder="https://…"
                    style={input}
                  />
                </Field>
              )}

              {form.questionType === "CODING" && (
                <CodingTypeBanner
                  questionId={form.questionId}
                  onOpen={() => {
                    if (form.questionId) navigate(`/admin/coding-questions/${form.questionId}`);
                  }}
                />
              )}

              {form.questionType === "SKILL" && (
                <SkillMetadata
                  value={form.typeMetadata}
                  onChange={(meta) => update("typeMetadata", meta)}
                />
              )}

              <Field label="Flags">
                <label style={toggleRow}>
                  <input
                    type="checkbox"
                    checked={form.terminateInterview}
                    onChange={(e) => update("terminateInterview", e.target.checked)}
                  />
                  Terminate interview if answered incorrectly
                </label>
                <label style={toggleRow}>
                  <input
                    type="checkbox"
                    checked={form.isMoveToQuestionBank}
                    onChange={(e) => update("isMoveToQuestionBank", e.target.checked)}
                  />
                  Save to question bank for reuse
                </label>
                <label style={toggleRow}>
                  <input
                    type="checkbox"
                    checked={!!form.isPractice}
                    onChange={(e) => update("isPractice", e.target.checked)}
                  />
                  Practice question (untimed, not scored)
                </label>
              </Field>

              {saveError && (
                <div style={errorBanner}>{saveError}</div>
              )}
              {createTemplateError && (
                <div style={errorBanner}>Template error: {createTemplateError}</div>
              )}
              {templateSaved && (
                <div style={successBanner}>
                  Saved to template library. Switch to the Templates tab to see it.
                </div>
              )}
            </div>

            <footer style={footer}>
              <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                <button onClick={handleSwitchToAdvanced} style={advancedLink}>
                  Switch to advanced editor →
                </button>
                <button
                  onClick={handleSaveAsTemplate}
                  disabled={!form.questionText.trim() || creatingTemplate}
                  style={{
                    ...advancedLink,
                    color: form.questionText.trim() && !creatingTemplate ? "#0F766E" : "#94A3B8",
                    cursor: form.questionText.trim() && !creatingTemplate ? "pointer" : "not-allowed",
                  }}
                >
                  {creatingTemplate ? "Saving template…" : "Save as template"}
                </button>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={onClose} style={ghostBtn} disabled={saving}>
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={!canSave() || saving}
                  style={{
                    ...primaryBtn,
                    opacity: !canSave() || saving ? 0.5 : 1,
                    cursor: !canSave() || saving ? "not-allowed" : "pointer",
                  }}
                >
                  {saving ? "Saving…" : form.questionId ? "Save changes" : "Create question"}
                </button>
              </div>
            </footer>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}

// ── Type-specific metadata sub-forms (feature #2) ────────────────────────
// `value` is whatever the parent has stored in form.typeMetadata. Could be:
//   - null (initial)
//   - an object (from local edits)
//   - a JSON string (from a freshly-loaded backend row — it's JSONB on the wire)
function asObject(v) {
  if (v == null) return {};
  if (typeof v === "object") return v;
  if (typeof v === "string") {
    try { return JSON.parse(v) || {}; } catch { return {}; }
  }
  return {};
}

// Banner shown when admin picks Coding as the question type. Coding config
// (starter code, solution, test cases, hidden flags, weights) lives in the
// dedicated /admin/coding-questions/{id} editor because the legacy inline
// typeMetadata JSON isn't read by the candidate flow or the grading service —
// those only consume amplehire_coding_question_t + amplehire_coding_test_case_t.
function CodingTypeBanner({ questionId, onOpen }) {
  return (
    <div style={{
      background: "#EEF2FF",
      border: "1px solid #C7D2FE",
      borderRadius: 8,
      padding: 12,
      fontSize: 12,
      color: "#3730A3",
    }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>Coding config lives in a dedicated editor</div>
      <div style={{ marginBottom: 8 }}>
        {questionId
          ? "Open the coding editor to author starter code, reference solution, and test cases."
          : "Save this question first — you'll be taken to the coding editor automatically."}
      </div>
      {questionId && (
        <button
          type="button"
          onClick={onOpen}
          style={{
            background: "#7B5AC4",
            color: "white",
            border: "none",
            borderRadius: 4,
            padding: "4px 10px",
            fontSize: 11,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          Open coding editor →
        </button>
      )}
    </div>
  );
}

// Legacy inline CodingMetadata removed — replaced by the dedicated editor
// at /admin/coding-questions/{questionId}. The old typeMetadata JSON path
// was never read by the candidate flow or grading service (those query
// amplehire_coding_question_t + amplehire_coding_test_case_t directly).

function SkillMetadata({ value, onChange }) {
  const meta = asObject(value);
  const patch = (p) => onChange({ ...meta, ...p });
  return (
    <Field label="Rating scale">
      <div style={chipRow}>
        {SKILL_SCALES.map((s) => (
          <Chip
            key={s}
            active={Number(meta.ratingScale || 5) === s}
            onClick={() => patch({ ratingScale: s })}
          >
            1–{s}
          </Chip>
        ))}
      </div>
    </Field>
  );
}

const Field = ({ label, children }) => (
  <div style={{ marginBottom: 14 }}>
    <div style={fieldLabel}>{label}</div>
    {children}
  </div>
);

const Row = ({ children }) => (
  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
    {children}
  </div>
);

const Chip = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    style={{
      padding: "6px 12px",
      borderRadius: 100,
      fontSize: 11,
      fontWeight: 600,
      border: active ? "1px solid #7B5AC4" : "1px solid #E2E8F0",
      background: active ? "#EEF2FF" : "white",
      color: active ? "#4338CA" : "#64748B",
      cursor: "pointer",
    }}
  >
    {children}
  </button>
);

const backdrop = {
  position: "fixed",
  inset: 0,
  background: "rgba(15, 23, 42, 0.4)",
  zIndex: 90,
};
const panel = {
  position: "fixed",
  top: 0,
  right: 0,
  bottom: 0,
  width: 480,
  background: "white",
  boxShadow: "-12px 0 32px rgba(15, 23, 42, 0.12)",
  zIndex: 100,
  display: "flex",
  flexDirection: "column",
};
const hdr = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  padding: "16px 20px",
  borderBottom: "1px solid #E2E8F0",
};
const hdrTitle = { fontSize: 16, fontWeight: 700, color: "#0F172A" };
const hdrSub = { fontSize: 11, color: "#64748B", marginTop: 2 };
const closeBtn = {
  background: "none",
  border: "none",
  fontSize: 24,
  lineHeight: 1,
  color: "#94A3B8",
  cursor: "pointer",
  padding: 4,
};
const body = {
  flex: 1,
  overflowY: "auto",
  padding: "16px 20px",
};
const footer = {
  padding: "12px 20px",
  borderTop: "1px solid #E2E8F0",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  background: "#F8FAFC",
};
const fieldLabel = {
  fontSize: 10,
  color: "#64748B",
  marginBottom: 4,
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: 0.4,
};
const input = {
  width: "100%",
  padding: "7px 10px",
  border: "1px solid #E2E8F0",
  borderRadius: 6,
  fontSize: 13,
  color: "#0F172A",
  outline: "none",
  background: "white",
  boxSizing: "border-box",
};
const textarea = { ...input, fontFamily: "inherit", resize: "vertical" };
const chipRow = { display: "flex", flexWrap: "wrap", gap: 6 };
const tagPill = {
  background: "#EEF2FF",
  color: "#4338CA",
  padding: "3px 8px",
  borderRadius: 100,
  fontSize: 11,
  fontWeight: 600,
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
};
const tagX = {
  background: "none",
  border: "none",
  color: "#4338CA",
  cursor: "pointer",
  fontSize: 14,
  lineHeight: 1,
  padding: 0,
};
const toggleRow = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  fontSize: 12,
  color: "#475569",
  marginBottom: 6,
  cursor: "pointer",
};
const advancedLink = {
  background: "none",
  border: "none",
  color: "#7B5AC4",
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
  padding: 0,
};
const primaryBtn = {
  padding: "8px 16px",
  borderRadius: 6,
  background: "#7B5AC4",
  color: "white",
  border: "none",
  fontWeight: 600,
  fontSize: 12,
  cursor: "pointer",
};
const ghostBtn = {
  padding: "8px 14px",
  borderRadius: 6,
  background: "white",
  color: "#475569",
  border: "1px solid #E2E8F0",
  fontWeight: 600,
  fontSize: 12,
  cursor: "pointer",
};
const ghostBtnSm = { ...ghostBtn, padding: "5px 10px", fontSize: 11 };
const errorBanner = {
  background: "#FEE2E2",
  color: "#991B1B",
  padding: "8px 10px",
  borderRadius: 6,
  fontSize: 12,
  marginTop: 8,
};
const successBanner = {
  background: "#DCFCE7",
  color: "#166534",
  padding: "8px 10px",
  borderRadius: 6,
  fontSize: 12,
  marginTop: 8,
};
